/**
 * Employee Service
 * API integration for employee CRUD operations
 */

import apiClient from './apiClient';
import { 
  Employee,
  CreateEmployeeRequest,
  UpdateEmployeeRequest,
  EmployeeFilters,
  EmployeeStats,
  EmployeeListParams
} from '../types/employee';
import { PaginatedResponse } from '../types/common';

// ==================== IMPORT PREVIEW TYPES ====================

export type { UnresolvedTerm, AvailableOption, ImportPreviewResult } from '../types/import';
import type { ImportPreviewResult } from '../types/import';

export interface EmployeeImportMappings {
  cargo_nome?: Record<string, number>;
  genero?: Record<string, string>;
  estado_civil?: Record<string, string>;
}

/**
 * Helper para extrair dados da resposta da API
 * Backend retorna: { message: "...", data: {...} }
 */
const extractData = (response: any) => {
  return response.data?.data || response.data;
};

class EmployeeService {
  private baseUrl = '/employees';

  /**
   * Get paginated list of employees
   */
  async getEmployees(params: EmployeeListParams = {}): Promise<PaginatedResponse<Employee>> {
    const { page = 1, limit = 20, sortBy = 'name', sortOrder = 'ASC', filters } = params;
    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder
    });

    if (filters) {
      if (filters.search) queryParams.append('search', filters.search);
      
      // Status filter (pode ser array)
      if (filters.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        statuses.forEach(s => queryParams.append('status', s));
      }
      
      // Gender filter (pode ser array)
      if (filters.gender) {
        const genders = Array.isArray(filters.gender) ? filters.gender : [filters.gender];
        genders.forEach(g => queryParams.append('gender', g));
      }
      
      // Company filter
      if (filters.companyId) {
        const companies = Array.isArray(filters.companyId) ? filters.companyId : [filters.companyId];
        companies.forEach(c => queryParams.append('companyId', c.toString()));
      }
      
      // Department filter
      if (filters.departmentId) {
        const departments = Array.isArray(filters.departmentId) ? filters.departmentId : [filters.departmentId];
        departments.forEach(d => queryParams.append('departmentId', d.toString()));
      }
      
      // Occupation filter
      if (filters.occupationId) {
        const occupations = Array.isArray(filters.occupationId) ? filters.occupationId : [filters.occupationId];
        occupations.forEach(o => queryParams.append('occupationId', o.toString()));
      }
      
      // Location filters
      if (filters.state) {
        const states = Array.isArray(filters.state) ? filters.state : [filters.state];
        states.forEach(s => queryParams.append('state', s));
      }
      if (filters.city) queryParams.append('city', filters.city);
      
      // Boolean filters
      if (filters.hasDisability !== undefined) {
        queryParams.append('hasDisability', filters.hasDisability.toString());
      }
      if (filters.isMinor !== undefined) {
        queryParams.append('isMinor', filters.isMinor.toString());
      }
      
      // Age range
      if (filters.minAge) queryParams.append('minAge', filters.minAge.toString());
      if (filters.maxAge) queryParams.append('maxAge', filters.maxAge.toString());
    }

    const response = await apiClient.get(`${this.baseUrl}?${queryParams}`);
    
    console.log('📦 [employeeService] Raw response:', response.data);
    
    // Backend retorna: { message: "...", data: { employees: Employee[], total: number, page, limit, totalPages } }
    const responseData = response.data.data || response.data;
    
    return {
      data: responseData.employees ?? [],
      pagination: {
        total: responseData.total ?? 0,
        page,
        limit,
        totalPages: responseData.totalPages ?? 0
      }
    };
  }

  /**
   * Get employee by ID
   */
  async getEmployeeById(id: number, includeRelations = true): Promise<Employee> {
    const params = includeRelations ? '?include=relations' : '';
    const response = await apiClient.get(`${this.baseUrl}/${id}${params}`);
    console.log('👤 [employeeService] Employee data:', extractData(response));
    return extractData(response);
  }

  /**
   * Get employee by CPF
   */
  async getEmployeeByCPF(cpf: string): Promise<Employee | null> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/cpf/${cpf}`);
      return extractData(response);
    } catch (error) {
      return null;
    }
  }

  /**
   * Search employee by CPF or employeeCode (cross-company, tenant-scoped)
   */
  async searchByIdentifier(query: string): Promise<Employee | null> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/search-by-identifier`, {
        params: { q: query },
      });
      return extractData(response);
    } catch (error: any) {
      if (error.status === 404 || error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create new employee
   */
  async createEmployee(data: CreateEmployeeRequest): Promise<Employee> {
    console.log('👤 Creating employee:', { ...data, cpf: data.cpf.replace(/\d/g, '*') });
    const response = await apiClient.post(this.baseUrl, data);
    console.log('✅ Employee created successfully:', response.data);
    return extractData(response);
  }

  /**
   * Update existing employee
   */
  async updateEmployee(id: number, data: UpdateEmployeeRequest): Promise<Employee> {
    console.log('👤 Updating employee:', { id, data: { ...data, cpf: data.cpf?.replace(/\d/g, '*') } });
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    console.log('✅ Employee updated successfully:', response.data);
    return extractData(response);
  }

  /**
   * Delete employee
   */
  async deleteEmployee(id: number): Promise<void> {
    console.log('🗑️ Deleting employee:', id);
    await apiClient.delete(`${this.baseUrl}/${id}`);
    console.log('✅ Employee deleted successfully');
  }

  /**
   * Get employees by company
   */
  async getEmployeesByCompany(companyId: number): Promise<Employee[]> {
    const response = await apiClient.get(`${this.baseUrl}/company/${companyId}`);
    return extractData(response);
  }

  /**
   * T11 da #408 — os funcionários que ficaram sem empresa. Só o contexto Plataforma alcança
   * essa lista; nos demais o recorte de tenant já a devolve vazia.
   */
  async getEmployeesWithoutCompany(): Promise<Employee[]> {
    const response = await apiClient.get(`${this.baseUrl}/without-company`);
    return extractData(response);
  }

  /**
   * Get employees by department
   */
  async getEmployeesByDepartment(departmentId: number): Promise<Employee[]> {
    const response = await apiClient.get(`${this.baseUrl}/department/${departmentId}`);
    return extractData(response);
  }

  /**
   * Get employees by occupation
   */
  async getEmployeesByOccupation(occupationId: number): Promise<Employee[]> {
    const response = await apiClient.get(`${this.baseUrl}/occupation/${occupationId}`);
    return extractData(response);
  }

  /**
   * Get employee statistics
   */
  async getEmployeeStats(): Promise<EmployeeStats> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    console.log('📊 [employeeService] Stats response:', response.data);
    // Backend retorna: { message: "...", data: { ATIVO: 13, DEMITIDO: 1, ... } }
    return response.data.data || response.data;
  }

  /**
   * Activate employee
   */
  async activateEmployee(id: number): Promise<Employee> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/activate`);
    return extractData(response);
  }

  /**
   * Suspend employee
   */
  async suspendEmployee(id: number): Promise<Employee> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/suspend`);
    return extractData(response);
  }

  /**
   * Put employee on leave
   */
  async putOnLeave(id: number): Promise<Employee> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/leave`);
    return extractData(response);
  }

  /**
   * Upload employee avatar
   */
  async uploadAvatar(id: number, file: File): Promise<{ avatarUrl: string }> {
    const formData = new FormData();
    formData.append('avatar', file);
    
    const response = await apiClient.post(`${this.baseUrl}/${id}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    return extractData(response);
  }

  /**
   * Preview import — parse Excel and return unresolved terms for mapping
   */
  async previewImport(
    file: File,
    options?: { companyId?: number }
  ): Promise<ImportPreviewResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.companyId) {
      formData.append('companyId', String(options.companyId));
    }

    const response = await apiClient.post(`${this.baseUrl}/import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    return extractData(response);
  }

  /**
   * Import employees from Excel
   */
  async importEmployees(
    file: File,
    options?: { companyId?: number; mappings?: EmployeeImportMappings }
  ): Promise<{
    success: number;
    errors: Array<{ line: number; error: string }>;
    warnings?: Array<{ line: number; warning: string }>;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.companyId) {
      formData.append('companyId', String(options.companyId));
    }
    if (options?.mappings) {
      formData.append('mappings', JSON.stringify(options.mappings));
    }

    const response = await apiClient.post(`${this.baseUrl}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    return extractData(response);
  }

  /**
   * Export employees to Excel
   */
  async exportEmployees(filters?: EmployeeFilters): Promise<Blob> {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }
    
    const response = await apiClient.get(`${this.baseUrl}/export?${queryParams}`, {
      responseType: 'blob'
    });

    return response as unknown as Blob;
  }

  /**
   * Download import template
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/template`, {
      responseType: 'blob'
    });

    return response as unknown as Blob;
  }

  /**
   * Validate CPF uniqueness
   */
  async validateCPF(cpf: string, excludeId?: number, companyId?: number): Promise<{ valid: boolean; message?: string }> {
    const params = excludeId ? `?excludeId=${excludeId}` : '';
    try {
      const response = await apiClient.post(`${this.baseUrl}/validate-cpf${params}`, { cpf, companyId });
      return extractData(response);
    } catch (error: any) {
      const status = error.response?.status;
      // Only treat 400/409 as validation errors; other errors (404, 500, network) should not block the form
      if (status === 400 || status === 409) {
        return {
          valid: false,
          message: error.response?.data?.error || 'CPF já cadastrado no sistema'
        };
      }
      // For network errors or unexpected responses, assume valid (server-side will validate on save)
      return { valid: true };
    }
  }
}

export const employeeService = new EmployeeService();
export default employeeService;
